// Comprehensive authentication debugging utility
import { supabase } from '@/integrations/supabase/client';

interface DebugLog {
  timestamp: string;
  level: 'info' | 'warn' | 'error' | 'debug';
  category: string;
  message: string;
  data?: any;
}

interface NetworkRequest {
  id: string;
  url: string;
  method: string;
  headers: Record<string, string>;
  body?: any;
  timestamp: string;
  response?: {
    status: number;
    statusText: string;
    headers: Record<string, string>;
    data?: any;
  };
}

interface EnvironmentInfo {
  url: string;
  hostname: string;
  protocol: string;
  pathname: string;
  origin: string;
  userAgent: string;
  isDevelopmentConsole: boolean;
  isExternalUrl: boolean;
  timestamp: string;
}

class AuthDebugger {
  private static instance: AuthDebugger;
  private logs: DebugLog[] = [];
  private networkRequests: NetworkRequest[] = [];
  private listeners: Set<(logs: DebugLog[]) => void> = new Set();
  private networkListeners: Set<(requests: NetworkRequest[]) => void> = new Set();
  private originalFetch: typeof fetch;
  private isEnabled = false;

  private constructor() {
    this.originalFetch = window.fetch.bind(window);
    this.initializeNetworkInterception();
  }

  static getInstance(): AuthDebugger {
    if (!AuthDebugger.instance) {
      AuthDebugger.instance = new AuthDebugger();
    }
    return AuthDebugger.instance;
  }

  enable() {
    this.isEnabled = true;
    this.log('info', 'AUTH_DEBUG', 'Authentication debugging enabled');
    this.logEnvironmentInfo();
  }

  disable() {
    this.isEnabled = false;
    this.log('info', 'AUTH_DEBUG', 'Authentication debugging disabled');
  }

  private log(level: DebugLog['level'], category: string, message: string, data?: any) {
    if (!this.isEnabled) return;

    const logEntry: DebugLog = {
      timestamp: new Date().toISOString(),
      level,
      category,
      message,
      data
    };

    this.logs.push(logEntry);
    
    // Keep only last 100 logs to prevent memory issues
    if (this.logs.length > 100) {
      this.logs = this.logs.slice(-100);
    }

    // Console logging for development
    const consoleMethod = level === 'error' ? 'error' : level === 'warn' ? 'warn' : 'log';
    console[consoleMethod](`[AUTH_DEBUG:${category}] ${message}`, data || '');

    this.notifyLogListeners();
  }

  getEnvironmentInfo(): EnvironmentInfo {
    const url = window.location.href;
    const hostname = window.location.hostname;
    const isDevelopmentConsole = hostname.includes('id-preview') || url.includes('id-preview');
    const isExternalUrl = hostname.includes('preview--dialgia-group') || url.includes('preview--dialgia-group');

    return {
      url,
      hostname,
      protocol: window.location.protocol,
      pathname: window.location.pathname,
      origin: window.location.origin,
      userAgent: navigator.userAgent,
      isDevelopmentConsole,
      isExternalUrl,
      timestamp: new Date().toISOString()
    };
  }

  private logEnvironmentInfo() {
    const envInfo = this.getEnvironmentInfo();
    this.log('info', 'ENV_DETECTION', 'Environment detected', envInfo);

    // Check localStorage state
    const authKeys = Object.keys(localStorage).filter(key => 
      key.startsWith('supabase.auth.') || key.includes('sb-')
    );
    
    this.log('info', 'STORAGE_STATE', 'LocalStorage auth keys found', {
      keys: authKeys,
      keyCount: authKeys.length
    });
  }

  private initializeNetworkInterception() {
    window.fetch = async (...args) => {
      if (!this.isEnabled) {
        return this.originalFetch(...args);
      }

      const [url, options = {}] = args;
      const urlString = typeof url === 'string' ? url : url.toString();
      
      // Only track Supabase requests
      if (!urlString.includes('supabase.co')) {
        return this.originalFetch(...args);
      }

      const requestId = `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      const timestamp = new Date().toISOString();

      const networkRequest: NetworkRequest = {
        id: requestId,
        url: urlString,
        method: options.method || 'GET',
        headers: this.extractHeaders(options.headers),
        body: options.body ? this.tryParseBody(options.body) : undefined,
        timestamp
      };

      this.networkRequests.push(networkRequest);
      this.log('debug', 'NETWORK_REQUEST', `Making request to ${urlString}`, {
        id: requestId,
        method: networkRequest.method,
        headers: networkRequest.headers
      });

      try {
        const response = await this.originalFetch(...args);
        const responseClone = response.clone();
        
        // Try to parse response
        let responseData;
        try {
          responseData = await responseClone.json();
        } catch {
          responseData = await responseClone.text();
        }

        networkRequest.response = {
          status: response.status,
          statusText: response.statusText,
          headers: this.extractHeaders(response.headers),
          data: responseData
        };

        this.log(
          response.ok ? 'debug' : 'error',
          'NETWORK_RESPONSE',
          `Response from ${urlString} - ${response.status}`,
          {
            id: requestId,
            status: response.status,
            data: responseData
          }
        );

        this.notifyNetworkListeners();
        return response;
      } catch (error) {
        this.log('error', 'NETWORK_ERROR', `Network error for ${urlString}`, {
          id: requestId,
          error: error instanceof Error ? error.message : String(error)
        });
        
        this.notifyNetworkListeners();
        throw error;
      }
    };
  }

  private extractHeaders(headers: any): Record<string, string> {
    if (!headers) return {};
    
    if (headers instanceof Headers) {
      const result: Record<string, string> = {};
      headers.forEach((value, key) => {
        result[key] = value;
      });
      return result;
    }
    
    return headers || {};
  }

  private tryParseBody(body: any): any {
    if (typeof body === 'string') {
      try {
        return JSON.parse(body);
      } catch {
        return body;
      }
    }
    return body;
  }

  // Public methods for logging from other components
  logAuthStep(step: string, data?: any) {
    this.log('info', 'AUTH_FLOW', step, data);
  }

  logAuthError(error: any, context?: string) {
    this.log('error', 'AUTH_ERROR', context || 'Authentication error', {
      error: error instanceof Error ? error.message : String(error),
      stack: error instanceof Error ? error.stack : undefined
    });
  }

  logSupabaseConfig() {
    this.log('info', 'SUPABASE_CONFIG', 'Supabase client configuration', {
      supabaseUrl: (supabase as any).supabaseUrl,
      supabaseKey: (supabase as any).supabaseKey?.substring(0, 20) + '...',
      authSettings: (supabase as any).auth?.settings,
      realtime: (supabase as any).realtime?.isConnected()
    });
  }

  // Subscription methods
  subscribeLogs(listener: (logs: DebugLog[]) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  subscribeNetworkRequests(listener: (requests: NetworkRequest[]) => void): () => void {
    this.networkListeners.add(listener);
    return () => this.networkListeners.delete(listener);
  }

  private notifyLogListeners() {
    this.listeners.forEach(listener => listener([...this.logs]));
  }

  private notifyNetworkListeners() {
    this.networkListeners.forEach(listener => listener([...this.networkRequests]));
  }

  // Getters
  getLogs(): DebugLog[] {
    return [...this.logs];
  }

  getNetworkRequests(): NetworkRequest[] {
    return [...this.networkRequests];
  }

  clear() {
    this.logs = [];
    this.networkRequests = [];
    this.notifyLogListeners();
    this.notifyNetworkListeners();
    this.log('info', 'AUTH_DEBUG', 'Debug logs cleared');
  }

  // Export debug data
  exportDebugData() {
    const envInfo = this.getEnvironmentInfo();
    const debugData = {
      timestamp: new Date().toISOString(),
      environment: envInfo,
      logs: this.logs,
      networkRequests: this.networkRequests,
      authState: {
        hasSession: !!localStorage.getItem('supabase.auth.token'),
        authKeys: Object.keys(localStorage).filter(key => 
          key.startsWith('supabase.auth.') || key.includes('sb-')
        )
      }
    };

    const blob = new Blob([JSON.stringify(debugData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `auth-debug-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }
}

export const authDebugger = AuthDebugger.getInstance();
export type { DebugLog, NetworkRequest, EnvironmentInfo };
